import { devLog } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { 
  Video, MessageCircle, Phone, Mail, Clock, Calendar,
  Loader2, ArrowLeft, CheckCircle, Star, CreditCard, ShieldCheck, QrCode
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const sessionTypes = [
  { id: 'chat', label: 'Chat', description: 'Conversa por texto em tempo real', icon: MessageCircle, color: 'bg-[#4A5D4E]' },
  { id: 'video', label: 'Vídeo', description: 'Videochamada com o profissional', icon: Video, color: 'bg-[#6B8E7C]' },
  { id: 'call', label: 'Ligação', description: 'Atendimento por telefone', icon: Phone, color: 'bg-[#D4A373]' },
  { id: 'email', label: 'Email', description: 'Orientação por escrito', icon: Mail, color: 'bg-[#9B8BAE]' }
];

const durations = [
  { value: 30, label: '30 min', discount: 0 },
  { value: 50, label: '50 min', discount: 0, popular: true },
  { value: 90, label: '90 min', discount: 10 }
];

export default function BookSessionPage() {
  const { professionalId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  
  const [selectedType, setSelectedType] = useState('chat');
  const [selectedDuration, setSelectedDuration] = useState(50);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  
  // PayPal flow state
  const [sessionCreated, setSessionCreated] = useState(null);
  const [paypalProcessing, setPaypalProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  useEffect(() => {
    fetchProfessional();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalId]);

  const fetchProfessional = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/professionals/${professionalId}`);
      setProfessional(data);
    } catch (err) {
      devLog.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = useCallback(() => {
    if (!professional) return 0;
    const sessionPrices = professional.session_prices || {};
    if (sessionPrices[selectedType]) {
      return parseFloat(sessionPrices[selectedType]);
    }
    const hourlyRate = professional.hourly_rate || 150;
    const basePrice = (hourlyRate / 60) * selectedDuration;
    const duration = durations.find(d => d.value === selectedDuration);
    const discount = duration?.discount || 0;
    return basePrice * (1 - discount / 100);
  }, [professional, selectedType, selectedDuration]);

  const handleBooking = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setBooking(true);
    setPaymentError('');

    try {
      const scheduledAt = scheduledDate && scheduledTime 
        ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString() 
        : null;

      const { data } = await axios.post(
        `${API_URL}/api/sessions/book`,
        {
          professional_id: professionalId,
          session_type: selectedType,
          duration_minutes: selectedDuration,
          scheduled_at: scheduledAt,
          notes: notes || null,
          origin_url: window.location.origin,
          payment_method: paymentMethod
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );

      if ((paymentMethod === 'stripe' || paymentMethod === 'pix') && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (paymentMethod === 'paypal' && data.session) {
        setSessionCreated(data.session);
      }
    } catch (err) {
      devLog.error(err);
      setPaymentError(err.response?.data?.detail || 'Erro ao criar sessão. Tente novamente.');
    } finally {
      setBooking(false);
    }
  };

  const handlePayPalCreateOrder = async () => {
    if (!sessionCreated) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/api/paypal/create-order`,
        { session_id: sessionCreated.session_id },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      return data.order_id;
    } catch (err) {
      devLog.error(err);
      setPaymentError('Erro ao criar pedido PayPal. Tente novamente.');
      throw err;
    }
  };

  const handlePayPalApprove = async (data) => {
    if (!sessionCreated) return;
    setPaypalProcessing(true);
    try {
      const { data: captureData } = await axios.post(
        `${API_URL}/api/paypal/capture-order`,
        { order_id: data.orderID, session_id: sessionCreated.session_id },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      if (captureData.status === 'COMPLETED') {
        navigate(`/session/${sessionCreated.session_id}?payment=success`);
      } else {
        setPaymentError('Pagamento não foi completado. Tente novamente.');
      }
    } catch (err) {
      devLog.error(err);
      setPaymentError('Erro ao capturar pagamento. Tente novamente.');
    } finally {
      setPaypalProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#4A5D4E] animate-spin" />
        </main>
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-[#B85C5C]">Profissional não encontrado</p>
        </main>
        <Footer />
      </div>
    );
  }

  const price = calculatePrice();

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />

      <main className="flex-1 py-12" data-testid="book-session-page">
        <div className="max-w-4xl mx-auto px-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-[#63635E] hover:text-[#292927] mb-8"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Booking Form */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h1 className="text-3xl font-light text-[#292927] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Agendar Sessão
                </h1>
                <p className="text-[#63635E]">com {professional.name}</p>
              </div>

              {/* Session Type */}
              <div className="card-theraflow p-6">
                <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Tipo de Sessão
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {sessionTypes.map((type) => {
                    const typePrice = professional.session_prices?.[type.id];
                    return (
                      <button
                        key={type.id}
                        onClick={() => { setSelectedType(type.id); setSessionCreated(null); }}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          selectedType === type.id
                            ? 'border-[#4A5D4E] bg-[#4A5D4E]/5'
                            : 'border-[#EAE8E3] hover:border-[#4A5D4E]/50'
                        }`}
                        data-testid={`type-${type.id}`}
                      >
                        <div className={`w-10 h-10 rounded-xl ${type.color} flex items-center justify-center mb-3`}>
                          <type.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                        </div>
                        <h3 className="font-medium text-[#292927]">{type.label}</h3>
                        <p className="text-xs text-[#63635E] mt-1">{type.description}</p>
                        {typePrice && (
                          <p className="text-sm font-semibold text-[#4A5D4E] mt-2">R$ {parseFloat(typePrice).toFixed(2)}</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Duration */}
              <div className="card-theraflow p-6">
                <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Duração
                </h2>
                <div className="flex gap-4">
                  {durations.map((dur) => (
                    <button
                      key={dur.value}
                      onClick={() => { setSelectedDuration(dur.value); setSessionCreated(null); }}
                      className={`flex-1 p-4 rounded-xl border-2 text-center relative transition-all ${
                        selectedDuration === dur.value
                          ? 'border-[#4A5D4E] bg-[#4A5D4E]/5'
                          : 'border-[#EAE8E3] hover:border-[#4A5D4E]/50'
                      }`}
                      data-testid={`duration-${dur.value}`}
                    >
                      {dur.popular && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-[#D4A373] text-white text-xs rounded-full">
                          Popular
                        </span>
                      )}
                      <Clock className="w-5 h-5 mx-auto mb-2 text-[#4A5D4E]" strokeWidth={1.5} />
                      <p className="font-medium text-[#292927]">{dur.label}</p>
                      {dur.discount > 0 && !professional.session_prices?.[selectedType] && (
                        <p className="text-xs text-[#6B8E7C] mt-1">-{dur.discount}%</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Schedule */}
              <div className="card-theraflow p-6">
                <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  <Calendar className="w-5 h-5 inline-block mr-2" strokeWidth={1.5} />
                  Agendar para
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-[#63635E]">Data</Label>
                    <Input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="input-theraflow mt-1"
                      data-testid="schedule-date"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#63635E]">Horário</Label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="input-theraflow mt-1"
                      data-testid="schedule-time"
                    />
                  </div>
                </div>
                <p className="text-xs text-[#A0A09A] mt-2">* O profissional confirmará o horário</p>
              </div>

              {/* Notes */}
              <div className="card-theraflow p-6">
                <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Observações (opcional)
                </h2>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Conte um pouco sobre o que você gostaria de trabalhar nessa sessão..."
                  className="input-theraflow min-h-[100px]"
                  data-testid="session-notes"
                />
              </div>
            </div>

            {/* Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="card-theraflow p-6 sticky top-24">
                {/* Professional Info */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-[#EAE8E3]">
                  {professional.photo_url ? (
                    <img src={professional.photo_url} alt={professional.name} className="w-14 h-14 rounded-xl object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-[#F6EBE6] flex items-center justify-center">
                      <span className="text-lg font-medium text-[#4A5D4E]">{professional.name?.charAt(0)}</span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium text-[#292927]">{professional.name}</h3>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-[#D4A373] fill-[#D4A373]" />
                      <span className="text-sm text-[#63635E]">
                        {professional.rating_avg?.toFixed(1)} ({professional.rating_count})
                      </span>
                    </div>
                  </div>
                </div>

                {/* Price Summary */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#63635E]">Tipo</span>
                    <span className="text-[#292927]">{sessionTypes.find(t => t.id === selectedType)?.label}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#63635E]">Duração</span>
                    <span className="text-[#292927]">{selectedDuration} min</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#EAE8E3] mb-6">
                  <div className="flex justify-between text-lg font-medium">
                    <span className="text-[#292927]">Total</span>
                    <span className="text-[#4A5D4E]" data-testid="total-price">R$ {price.toFixed(2)}</span>
                  </div>
                </div>

                {/* Payment Method Selector */}
                {!sessionCreated && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-[#292927] mb-3">Forma de pagamento</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        onClick={() => setPaymentMethod('stripe')}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          paymentMethod === 'stripe'
                            ? 'border-[#4A5D4E] bg-[#4A5D4E]/5'
                            : 'border-[#EAE8E3] hover:border-[#4A5D4E]/50'
                        }`}
                        data-testid="payment-stripe"
                      >
                        <CreditCard className="w-5 h-5 mx-auto mb-1.5 text-[#4A5D4E]" strokeWidth={1.5} />
                        <p className="text-xs font-medium text-[#292927]">Cartão</p>
                      </button>
                      <button
                        onClick={() => setPaymentMethod('pix')}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          paymentMethod === 'pix'
                            ? 'border-[#32BCAD] bg-[#32BCAD]/5'
                            : 'border-[#EAE8E3] hover:border-[#32BCAD]/50'
                        }`}
                        data-testid="payment-pix"
                      >
                        <QrCode className="w-5 h-5 mx-auto mb-1.5 text-[#32BCAD]" strokeWidth={1.5} />
                        <p className="text-xs font-medium text-[#292927]">Pix</p>
                      </button>
                      <button
                        onClick={() => setPaymentMethod('paypal')}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          paymentMethod === 'paypal'
                            ? 'border-[#0070BA] bg-[#0070BA]/5'
                            : 'border-[#EAE8E3] hover:border-[#0070BA]/50'
                        }`}
                        data-testid="payment-paypal"
                      >
                        <svg className="w-5 h-5 mx-auto mb-1.5" viewBox="0 0 24 24" fill="none">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.23A.773.773 0 0 1 5.706 1.6h6.865c2.14 0 3.643.56 4.473 1.668.39.52.624 1.084.711 1.724.095.668.036 1.468-.175 2.38l-.013.06v.54l.42.237c.354.186.637.39.858.626.345.37.568.833.668 1.372.102.55.08 1.204-.068 1.943-.169.848-.44 1.586-.815 2.194a4.62 4.62 0 0 1-1.226 1.382c-.488.362-1.065.63-1.72.808-.634.172-1.358.259-2.153.259h-.512a1.547 1.547 0 0 0-1.528 1.307l-.038.221-.645 4.082-.029.161a.254.254 0 0 1-.069.146.252.252 0 0 1-.16.058H7.076Z" fill="#253B80"/>
                          <path d="M19.583 7.728c-.012.078-.025.157-.04.24-.79 4.055-3.496 5.459-6.951 5.459h-1.758a.853.853 0 0 0-.844.723L9.37 18.4l-.2 1.262a.45.45 0 0 0 .444.52h3.117c.37 0 .684-.27.742-.635l.03-.157.588-3.731.038-.207a.747.747 0 0 1 .739-.635h.465c3.018 0 5.383-1.225 6.072-4.771.287-1.482.139-2.72-.622-3.59a2.981 2.981 0 0 0-.856-.617l-.344-.111Z" fill="#179BD7"/>
                          <path d="M18.647 7.356a6.36 6.36 0 0 0-.783-.174 9.928 9.928 0 0 0-1.578-.116h-4.784a.743.743 0 0 0-.736.635l-1.017 6.447-.03.189a.853.853 0 0 1 .844-.724h1.758c3.455 0 6.161-1.403 6.951-5.459.024-.12.043-.237.058-.35a4.08 4.08 0 0 0-.683-.448Z" fill="#222D65"/>
                        </svg>
                        <p className="text-xs font-medium text-[#292927]">PayPal</p>
                      </button>
                    </div>
                  </div>
                )}

                {/* Error message */}
                {paymentError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" data-testid="payment-error">
                    {paymentError}
                  </div>
                )}

                {/* PayPal Buttons (shown after session is created with PayPal method) */}
                {sessionCreated && paymentMethod === 'paypal' && PAYPAL_CLIENT_ID && (
                  <div className="mb-4" data-testid="paypal-buttons-container">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700 mb-3 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                      <span>Complete o pagamento via PayPal para confirmar sua sessão</span>
                    </div>
                    {paypalProcessing ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-6 h-6 text-[#0070BA] animate-spin" />
                        <span className="ml-2 text-sm text-[#63635E]">Processando pagamento...</span>
                      </div>
                    ) : (
                      <PayPalScriptProvider options={{ 
                        clientId: PAYPAL_CLIENT_ID,
                        currency: 'BRL',
                        intent: 'capture'
                      }}>
                        <PayPalButtons
                          style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay', height: 45 }}
                          createOrder={handlePayPalCreateOrder}
                          onApprove={handlePayPalApprove}
                          onError={(err) => {
                            devLog.error('PayPal error:', err);
                            setPaymentError('Erro no PayPal. Tente novamente.');
                          }}
                          onCancel={() => {
                            setPaymentError('Pagamento cancelado.');
                          }}
                        />
                      </PayPalScriptProvider>
                    )}
                  </div>
                )}

                {/* Confirm Button (Stripe or initial PayPal step) */}
                {!sessionCreated && (
                  <button
                    onClick={handleBooking}
                    disabled={booking || !user}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="confirm-booking"
                  >
                    {booking ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : paymentMethod === 'stripe' ? (
                      <>
                        <CreditCard className="w-5 h-5" />
                        Pagar com Cartão
                      </>
                    ) : paymentMethod === 'pix' ? (
                      <>
                        <QrCode className="w-5 h-5" />
                        Pagar com Pix
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Continuar com PayPal
                      </>
                    )}
                  </button>
                )}

                {!user && (
                  <p className="text-xs text-[#A0A09A] text-center mt-3">Faça login para agendar</p>
                )}

                <div className="flex items-center justify-center gap-1.5 mt-4">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#6B8E7C]" />
                  <p className="text-xs text-[#A0A09A]">Pagamento seguro e protegido</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
